const TEAM_PROFILE_PAGE = '/team-profile';

export type DistributionId = 'vidu' | 'enterprise';
/**
 * 路由规则字符串。支持三种形态：
 * - 精确路径：`/login`
 * - 通配前缀：`/create/**`（同时匹配 `/create` 和 `/create/...`）
 * - deny 子规则：以 `!` 开头（如 `!/create/ai-video-cutter`）。命中即拒绝，优先级高于 allow，
 *   用于在粗粒度 allow 下精准排除少数子路径。
 */
export type DistributionRouteRule = string;
export type LoginMethod = 'google' | 'apple' | 'email' | 'phone';

type DistributionRoutes =
  | {
      mode: 'all';
      allowlist: readonly DistributionRouteRule[];
    }
  | {
      mode: 'allowlist';
      allowlist: readonly DistributionRouteRule[];
      fallbackPath: string;
    };

export interface Distribution {
  /** 构建产物标识。业务代码应优先读取下方语义化策略，不直接按 id 分支。 */
  id: DistributionId;
  routes: DistributionRoutes;
}

const distributionId = process.env.NEXT_PUBLIC_DISTRIBUTION === 'enterprise' ? 'enterprise' : 'vidu';

// enterprise 发行版按白名单开放页面；未列出的现存路由会在 proxy 中兜底到团队页。
// 以 `!` 开头的规则是「deny 子规则」，命中即拒绝（优先级高于 allow）；
// 用于在保留 `/create/**` 这类粗粒度允许的同时，精准排除少数定制版不开放的子路径。
export const ENTERPRISE_ROUTE_ALLOWLIST = [
  '/login',
  '/banned',
  '/not-found',
  '/team-profile',
  '/team-profile/**',
  '/assets',
  '/assets/**',
  '/creation-detail/**',
  '/create/**',
  // 视频剪辑功能定制版不开放
  '!/create/ai-video-cutter',
  '!/create/ai-video-cutter/**',
  '/invite/**',
  '/share/**',
] as const satisfies readonly DistributionRouteRule[];

export const ENTERPRISE_ROUTE_FALLBACK_PATH = TEAM_PROFILE_PAGE;

const createBaseDistribution = (): Distribution => ({
  id: 'vidu',
  routes: {
    mode: 'all',
    allowlist: [],
  },
});

const createEnterpriseDistribution = (): Distribution => {
  const baseDistribution = createBaseDistribution();

  return {
    ...baseDistribution,
    id: 'enterprise',
    routes: {
      mode: 'allowlist',
      allowlist: ENTERPRISE_ROUTE_ALLOWLIST,
      fallbackPath: ENTERPRISE_ROUTE_FALLBACK_PATH,
    },
  };
};

export const DISTRIBUTIONS = {
  vidu: createBaseDistribution(),
  enterprise: createEnterpriseDistribution(),
} as const satisfies Record<DistributionId, Distribution>;

export const distribution: Distribution = DISTRIBUTIONS[distributionId];
export const isEnterpriseDistribution = distribution.id === 'enterprise';
export const ENTERPRISE_LOGO_URL = '/enterprise-logo.webp';
export const ENTERPRISE_WATERMARK_URL = 'https://image01.vidu.zone/vidu/media-asset/watermark-gen-custom-7a63dae8.webp';
// 定制版登录页背景视频与封面图（仅中国站定制版使用，海外站定制版仍走 SITE 分支）
export const ENTERPRISE_LOGIN_BG_VIDEO_URL = 'https://image01.vidu.zone/vidu/landing-page/banner3.64dda4ef.mp4';
export const ENTERPRISE_LOGIN_BG_POSTER_URL = 'https://image01.vidu.zone/vidu/media-asset/banner3-3b5b55f3.webp';
export const BROWSER_ICON_URL = isEnterpriseDistribution ? ENTERPRISE_LOGO_URL : '/favicon.ico';
